import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Send, Bell } from "lucide-react";

export default function NotificationsSend() {
    const [params] = useSearchParams();
    const { user } = useAuth();
    const [classes, setClasses] = useState([]);
    const [parents, setParents] = useState([]);
    const [students, setStudents] = useState([]);
    const [audience, setAudience] = useState(params.get("audience") || "all_parents");
    const [classId, setClassId] = useState("");
    const [targetId, setTargetId] = useState(params.get("target_id") || "");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [sendEmail, setSendEmail] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");
    const prefilledName = params.get("name") || "";

    useEffect(() => {
        (async () => {
            try {
                const [c, p, su] = await Promise.all([
                    api.get("/classes"),
                    api.get("/admin/parents").catch(() => ({ data: [] })),
                    api.get("/admin/student-users").catch(() => ({ data: [] })),
                ]);
                setClasses(c.data); setParents(p.data); setStudents(su.data);
            } catch (e) { /* ignore */ }
        })();
    }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setResult(null); setBusy(true);
        try {
            const payload = {
                audience, title, body, send_email: sendEmail,
                class_id: ["class_parents", "class_students"].includes(audience) ? classId : null,
                target_id: ["single_parent", "single_student"].includes(audience) ? targetId : null,
            };
            const r = await api.post("/notifications/send", payload);
            setResult(r.data);
            setTitle(""); setBody("");
        } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
        finally { setBusy(false); }
    };

    return (
        <Layout>
            <Link to={user?.role === "super_admin" ? "/admin" : "/teacher"} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-oxford mb-4">
                <ArrowLeft className="w-4 h-4" /> Mbrapa
            </Link>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Njoftime</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Dërgo Njoftim</h1>
                <p className="text-slate-600 mt-2 text-sm">Dërgo te prindërit ose nxënësit (in-app + opcional email)</p>
                {prefilledName && <div className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-oxford/10 text-oxford text-xs font-bold">Drejtuar: {prefilledName}</div>}
            </div>

            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-4 max-w-2xl" data-testid="notifications-form">
                <div>
                    <label className="text-xs font-bold uppercase tracking-widest text-slate-500 block mb-2">Audienca</label>
                    <select value={audience} onChange={(e) => setAudience(e.target.value)} className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="notif-audience">
                        <option value="all_parents">Të gjithë prindërit</option>
                        <option value="all_students">Të gjithë nxënësit</option>
                        <option value="class_parents">Prindërit e një klase</option>
                        <option value="class_students">Nxënësit e një klase</option>
                        <option value="single_parent">Një prind specifik</option>
                        <option value="single_student">Një nxënës specifik</option>
                    </select>
                </div>
                {(audience === "class_parents" || audience === "class_students") && (
                    <select value={classId} onChange={(e) => setClassId(e.target.value)} required className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="notif-class">
                        <option value="">— Zgjidh klasën —</option>
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
                {audience === "single_parent" && !prefilledName && (
                    <select value={targetId} onChange={(e) => setTargetId(e.target.value)} required className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="notif-target-parent">
                        <option value="">— Zgjidh prindin —</option>
                        {parents.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.email})</option>)}
                    </select>
                )}
                {audience === "single_student" && !prefilledName && (
                    <select value={targetId} onChange={(e) => setTargetId(e.target.value)} required className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm" data-testid="notif-target-student">
                        <option value="">— Zgjidh nxënësin —</option>
                        {students.map((s) => <option key={s.id} value={s.id}>{s.student_name || s.name} {s.class_name ? `• ${s.class_name}` : ""} ({s.email})</option>)}
                    </select>
                )}
                <Input placeholder="Titulli i njoftimit" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="notif-title" />
                <Textarea placeholder="Mesazhi…" value={body} onChange={(e) => setBody(e.target.value)} rows={5} required data-testid="notif-body" />
                <div className="flex items-center justify-between p-3 bg-bone/40 rounded-lg border border-slate-200">
                    <div>
                        <div className="font-bold text-sm text-slate-800">Dërgo gjithashtu si email</div>
                        <div className="text-xs text-slate-500">Përmes Resend (CC dgi.prishtina@gmail.com)</div>
                    </div>
                    <Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="notif-send-email" />
                </div>
                {err && <div className="text-sm text-terracotta bg-terracotta/10 border border-terracotta/30 rounded-lg px-3 py-2">{err}</div>}
                {result && <div className="text-sm bg-sage/10 border border-sage/30 text-sage rounded-lg px-3 py-2" data-testid="notif-result">U dërgua te <strong>{result.inserted}</strong> përdorues{result.email_sent > 0 ? ` (email: ${result.email_sent})` : ""}</div>}
                <Button type="submit" disabled={busy} className="bg-oxford hover:bg-oxfordhover text-white gap-2" data-testid="notif-submit">
                    <Send className="w-4 h-4" /> {busy ? "Duke dërguar…" : "Dërgo"}
                </Button>
            </form>
        </Layout>
    );
}
